import { useCallback } from 'react';
import { FieldValues, SubmitHandler, useFormContext } from 'react-hook-form';

import { CampaignState } from 'constants/campaignStates';
import { BUTTON_SIZES, Button } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { useCampaignContext } from '../../context';
import { ModalType, useModalContext } from '../../context/ModalContext';
import { ScheduleEligibilityFormState } from '../../types';
import {
  DisplayEligibilityCampaignForm,
  withDisplayEligibilityCampaignFormProvider,
} from './DisplayEligibilityCampaignForm';
import { useScheduleEligibilityCampaign } from './useScheduleEligibilityCampaign';

type DisplayEligibilityCampaignModalProps = {
  asDisplayNow?: boolean;
};

export const DisplayEligibilityCampaignModal = withDisplayEligibilityCampaignFormProvider(
  ({ asDisplayNow }: DisplayEligibilityCampaignModalProps) => {
    const {
      handleSubmit,
      formState: { isValid, isDirty },
    } = useFormContext();

    const { closeModal, openModal, openedModals } = useModalContext();
    const { campaign } = useCampaignContext();
    const { isLoading, scheduleEligibilityCampaign } = useScheduleEligibilityCampaign();

    const isCurrentModalDisplayEligibilityModal =
      openedModals?.[openedModals.length - 1]?.type === ModalType.DisplayEligibilityCampaign;

    const handleSchedule: SubmitHandler<FieldValues> = useCallback(
      (values: FieldValues) => {
        openModal(ModalType.ConfirmDisplayEligibilityCampaign, {
          onConfirm: () => scheduleEligibilityCampaign(values as ScheduleEligibilityFormState),
          values,
          isLoading,
        });
      },
      [scheduleEligibilityCampaign],
    );

    return (
      <Modal
        isModalOpen
        withPortal
        id="display-eligibility-campaign-modal"
        data-test="display-eligibility-campaign-modal"
        onClose={() => {
          if (isCurrentModalDisplayEligibilityModal) {
            if (!isDirty) {
              closeModal(ModalType.DisplayEligibilityCampaign);
            } else {
              openModal(ModalType.ScheduleUnsavedChangesModal);
            }
          }
        }}
        size={MODAL_SIZES.MEDIUM}
        variant={MODAL_VARIANTS.DEFAULT}
      >
        <ModalHeader>{asDisplayNow ? 'Start campaign now' : 'Schedule campaign'}</ModalHeader>
        <ModalBody>
          <DisplayEligibilityCampaignForm withStart={!asDisplayNow} />
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={
              !isValid ||
              /** For scheduled campaigns, we are disallowing updates if no changes have been made to the saved schedule */
              (campaign.campaignState === CampaignState.Scheduled && !isDirty)
            }
            onClick={handleSubmit(handleSchedule)}
            data-test="schedule-display-eligibility-campaign-button"
            isLoading={isLoading}
            size={BUTTON_SIZES.LARGE}
          >
            {asDisplayNow && 'Start '}
            {!asDisplayNow && (
              <>{campaign.campaignState === CampaignState.Scheduled ? 'Reschedule' : 'Schedule'} </>
            )}
            campaign
          </Button>
        </ModalFooter>
      </Modal>
    );
  },
);
